import {Dispatch, FC, ReactNode, SetStateAction, useEffect, useMemo, useState} from 'react'
import {
  TaxFolderPdfConfirmResult,
  TaxFolderReferenceCandidate,
  TaxIdOwnershipValidationResult,
  validateTaxFolderTaxId,
} from '../core/_requests'
import {
  INVOICE_SUBJECTS,
  InvoiceSubject,
  isValidReferenceTaxId,
  normalizeReferenceTaxId,
} from '../core/invoiceSubject'
import {useIsOnPremises} from '../../../../auth/core/useOnPremises'
import {InvoiceSubjectGuide} from './InvoiceSubjectGuide'
import {TaxFolderReferenceModal} from './TaxFolderReferenceModal'
import {TaxFolderPdfImportModal} from '../components/header/TaxFolderPdfImportModal'
import './InvoiceSubject.css'
import {FieldHelpLabel} from '../../../../../../_metronic/helpers/components/FieldHelp'

type Props = {
  subject: InvoiceSubject
  setSubject: Dispatch<SetStateAction<InvoiceSubject>>
  invoiceType: number
  referenceTaxId: string
  setReferenceTaxId: Dispatch<SetStateAction<string>>
  onReferenceOwnershipChange: (isValid: boolean) => void
  onSelectCandidate: (candidate: TaxFolderReferenceCandidate) => void
  onPdfImported: (result: TaxFolderPdfConfirmResult) => void
  allowedSubjects?: InvoiceSubject[]
  rulesContent?: ReactNode
}

const InvoiceSubjectSelector: FC<Props> = ({
  subject,
  setSubject,
  invoiceType,
  referenceTaxId,
  setReferenceTaxId,
  onReferenceOwnershipChange,
  onSelectCandidate,
  onPdfImported,
  allowedSubjects,
  rulesContent,
}) => {
  const onPremises = useIsOnPremises()
  const [expanded, setExpanded] = useState(false)
  const [showReferenceModal, setShowReferenceModal] = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [ownershipLoading, setOwnershipLoading] = useState(false)
  const [ownership, setOwnership] = useState<TaxIdOwnershipValidationResult>()
  // What happened when the chosen reference was registered from the Excel export.
  const [referenceNotice, setReferenceNotice] = useState<{taxId: string; text: string}>()
  const ownershipAllowed = ownership?.isValid === true && ownership?.canCreateReferral !== false
  const referenceIsValid = isValidReferenceTaxId(referenceTaxId)
  const availableSubjects = useMemo(
    () =>
      allowedSubjects?.length
        ? INVOICE_SUBJECTS.filter((option) => allowedSubjects.includes(option.id))
        : INVOICE_SUBJECTS,
    [allowedSubjects]
  )
  const selectedSubject = useMemo(
    () => availableSubjects.find((option) => option.id === subject) || availableSubjects[0],
    [availableSubjects, subject]
  )

  useEffect(() => {
    if (availableSubjects.length && !availableSubjects.some((option) => option.id === subject)) {
      setSubject(availableSubjects[0].id)
      setReferenceTaxId('')
    }
  }, [availableSubjects, setReferenceTaxId, setSubject, subject])

  useEffect(() => {
    let active = true
    setOwnership(undefined)

    if (subject === 1) {
      setOwnershipLoading(false)
      onReferenceOwnershipChange(true)
      return () => {
        active = false
      }
    }
    if (!referenceIsValid) {
      setOwnershipLoading(false)
      onReferenceOwnershipChange(false)
      return () => {
        active = false
      }
    }

    setOwnershipLoading(true)
    onReferenceOwnershipChange(false)
    const timer = window.setTimeout(() => {
      validateTaxFolderTaxId(referenceTaxId, subject)
        .then((result) => {
          if (!active) return
          setOwnership(result)
          const isAllowed = result.isValid && result.canCreateReferral !== false
          onReferenceOwnershipChange(isAllowed)
          if (
            isAllowed &&
            result.resolvedReferenceTaxId &&
            result.resolvedReferenceTaxId !== referenceTaxId
          ) {
            setReferenceTaxId(normalizeReferenceTaxId(result.resolvedReferenceTaxId))
          }
        })
        .catch((error) => {
          if (!active) return
          const message =
            error?.response?.data?.Message ||
            error?.response?.data?.message ||
            'امکان بررسی تعلق شناسه مالیاتی به کسب‌وکار وجود ندارد.'
          setOwnership({isValid: false, fiscalIdConfigured: false, message})
          onReferenceOwnershipChange(false)
        })
        .finally(() => {
          if (active) setOwnershipLoading(false)
        })
    }, 450)

    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [onReferenceOwnershipChange, referenceIsValid, referenceTaxId, subject])

  const selectSubject = (nextSubject: InvoiceSubject) => {
    setSubject(nextSubject)
    if (nextSubject === 1) {
      setReferenceTaxId('')
    }
  }

  return (
    <section className={`mp-invoice-subject-card ${expanded ? 'is-expanded' : 'is-collapsed'}`}>
      <button
        type='button'
        className='mp-invoice-subject-summary'
        aria-expanded={expanded}
        aria-controls='invoice-subject-details'
        onClick={() => setExpanded((current) => !current)}
      >
        <span className={`mp-invoice-subject-summary__icon is-${selectedSubject.tone}`}>
          <i className={`las ${selectedSubject.icon}`} aria-hidden='true'></i>
        </span>
        <span className='mp-invoice-subject-summary__content'>
          <small>
            {expanded ? (
              'موضوع صورتحساب'
            ) : (
              <FieldHelpLabel label='موضوع صورتحساب' helpKey='header.ins' />
            )}
          </small>
          <strong>{selectedSubject.title}</strong>
          <span>
            صورتحساب نوع {invoiceType === 1 ? 'اول' : 'دوم'}
            {subject !== 1 && referenceTaxId && (
              <em className='englishText'> · {referenceTaxId}</em>
            )}
          </span>
        </span>
        <span className='mp-invoice-subject-summary__action'>
          <small>{expanded ? 'بستن جزئیات' : 'مشاهده و تغییر'}</small>
          <i className={`las ${expanded ? 'la-angle-up' : 'la-angle-down'}`}></i>
        </span>
      </button>

      {expanded && (
        <div id='invoice-subject-details' className='mp-invoice-subject-card__body'>
          <div className='mp-invoice-subject-card__heading'>
            <div>
              <h3>
                <FieldHelpLabel label='موضوع صورتحساب را انتخاب کنید' helpKey='header.ins' />
              </h3>
              <p>موضوع انتخابی، فیلدها و قواعد ادامه فرم را تعیین می‌کند.</p>
            </div>
            <span className='badge badge-light-primary'>انتخاب فعلی: {selectedSubject.title}</span>
          </div>

          <div className='mp-invoice-subject-options' role='radiogroup' aria-label='موضوع صورتحساب'>
            {availableSubjects.map((option) => (
              <div key={option.id} className='mp-invoice-subject-option-wrap'>
                <button
                  type='button'
                  role='radio'
                  aria-checked={subject === option.id}
                  className={`mp-invoice-subject-option is-${option.tone} ${
                    subject === option.id ? 'is-selected' : ''
                  }`}
                  onClick={() => selectSubject(option.id)}
                >
                  <span className='mp-invoice-subject-option__number'>{option.id}</span>
                  <i className={`las ${option.icon}`} aria-hidden='true'></i>
                  <span>
                    <strong>{option.title}</strong>
                    <small>{option.subtitle}</small>
                  </span>
                  <i
                    className='las la-check-circle mp-invoice-subject-option__check'
                    aria-hidden='true'
                  ></i>
                </button>
              </div>
            ))}
          </div>

          {subject !== 1 && (
            <div className='mp-reference-field'>
              <div className='mp-reference-field__control'>
                <label htmlFor='reference-tax-id'>
                  <FieldHelpLabel
                    label='شناسه مالیاتی صورتحساب مرجع'
                    helpKey='header.irtaxid'
                    isRequired
                  />{' '}
                  <span>*</span>
                </label>
                <div
                  className={`input-group ${
                    referenceTaxId && (!referenceIsValid || (ownership && !ownershipAllowed))
                      ? 'is-invalid'
                      : ''
                  }`}
                  dir='ltr'
                >
                  <input
                    id='reference-tax-id'
                    className='form-control englishText text-uppercase'
                    value={referenceTaxId}
                    onChange={(event) => {
                      setOwnership(undefined)
                      onReferenceOwnershipChange(false)
                      setReferenceTaxId(normalizeReferenceTaxId(event.target.value))
                    }}
                    maxLength={22}
                    autoComplete='off'
                    spellCheck={false}
                    placeholder='22 CHARACTER TAX ID'
                  />
                  <span className='input-group-text'>{referenceTaxId.length}/22</span>
                </div>
                <div className='mp-reference-field__validation' aria-live='polite'>
                  {ownershipLoading ? (
                    <span className='is-loading'>
                      <i className='spinner-border spinner-border-sm'></i>
                      در حال بررسی شناسه یکتای حافظه مالیاتی...
                    </span>
                  ) : ownership ? (
                    <span className={ownershipAllowed ? 'is-valid' : 'is-invalid'}>
                      <i
                        className={`las ${
                          ownershipAllowed ? 'la-check-circle' : 'la-exclamation-circle'
                        }`}
                      ></i>
                      {ownership.message}
                    </span>
                  ) : (
                    <span className={referenceTaxId && !referenceIsValid ? 'is-invalid' : ''}>
                      دقیقاً ۲۲ کاراکتر؛ ۶ کاراکتر نخست با شناسه حافظه مالیاتی تنظیمات تطبیق داده
                      می‌شود.
                    </span>
                  )}
                </div>
                {referenceNotice && referenceNotice.taxId === referenceTaxId && (
                  <div
                    className='alert alert-info d-flex align-items-start gap-2 mt-3 mb-0 py-3 fs-7'
                    role='status'
                  >
                    <i className='las la-info-circle fs-3' aria-hidden='true'></i>
                    <span>{referenceNotice.text}</span>
                  </div>
                )}
              </div>
              {/* Both read کارپوشه's files; an on-premises install has no کارپوشه. */}
              {!onPremises && (
                <div className='mp-reference-field__actions'>
                  {(subject === 2 || subject === 3 || subject === 4) && (
                    <button
                      type='button'
                      className='btn btn-light-primary mp-reference-field__upload'
                      onClick={() => setShowReferenceModal(true)}
                    >
                      <i className='las la-file-excel fs-2'></i>
                      انتخاب از اکسل کارپوشه
                    </button>
                  )}
                  <button
                    type='button'
                    className='btn btn-light-danger mp-reference-field__upload'
                    onClick={() => setShowPdfModal(true)}
                  >
                    <i className='las la-file-pdf fs-2'></i>
                    ورود PDF تکی با اقلام
                  </button>
                </div>
              )}
            </div>
          )}

          <InvoiceSubjectGuide subject={subject} />

          {rulesContent}

          {subject === 3 && (
            <div className='mp-batch-cancellation-tip'>
              <i className='las la-layer-group'></i>
              <span>
                <strong>ابطال گروهی دارید؟</strong> از منوی «ابطال گروهی صورتحساب‌ها» می‌توانید
                فایل اکسل ابطال دسته‌ای را بارگذاری کنید.
              </span>
            </div>
          )}
        </div>
      )}

      {showReferenceModal && (
        <TaxFolderReferenceModal
          subject={subject}
          onClose={() => setShowReferenceModal(false)}
          onSelect={(candidate) => {
            setShowReferenceModal(false)
            setReferenceNotice(
              candidate.notice
                ? {taxId: normalizeReferenceTaxId(candidate.taxId), text: candidate.notice}
                : undefined
            )
            onSelectCandidate(candidate)
          }}
        />
      )}
      {showPdfModal && (
        <TaxFolderPdfImportModal
          onClose={() => setShowPdfModal(false)}
          onImported={(result) => {
            setShowPdfModal(false)
            onPdfImported(result)
          }}
        />
      )}
    </section>
  )
}

export {InvoiceSubjectSelector}
